import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { useDispatch } from "react-redux";
import { createEmbeddedCheckoutSession } from "../../Redux/actions/commonActtion";
import { New_ScrollX2025 } from "../../assets/icons";
import "./EmbeddedCheckoutPage.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const EmbeddedCheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState(null);

  const plan = location.state?.plan;

  const fetchClientSecret = useCallback(async () => {
    if (!plan?._id) throw new Error("No plan selected");
    try {
      const data = await dispatch(createEmbeddedCheckoutSession(plan._id));
      return data.clientSecret;
    } catch (err) {
      setError(err.message || "Failed to initialize checkout");
      throw err;
    }
  }, [dispatch, plan?._id]);

  useEffect(() => {
    if (!plan) {
      navigate("/plans", { replace: true });
    }
  }, [plan, navigate]);

  if (!plan) return null;

  const planSubtitles = {
    49: (
      <>
        Unlimited 4K renders and all broadcast frame rates.
        <br />
        Build for fast turnarounds and single titles.
      </>
    ),
    89: (
      <>
        From rough cuts to final delivery and festival versions.
        <br />
        Everything you need for a single film.
      </>
    ),
    199: (
      <>
        Rolling credits across multiple titles.
        <br />
        Priority rendering and custom font storage included.
      </>
    ),
  };

  const planSubtitle =
    plan.subtitle ||
    planSubtitles[Number(plan.price)] ||
    "Subscribe to unlock premium features. Cancel anytime before your trial ends — no charges.";

  const features = [
    plan.text1,
    plan.text2,
    plan.text3,
    plan.text4,
    plan.text6,
  ].filter(Boolean);

  if (error) {
    return (
      <div className="checkout-error-container">
        <div className="checkout-error-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>
        <h2 className="checkout-error-title">Something went wrong</h2>
        <p className="checkout-error-message">{error}</p>
        <button className="checkout-error-btn" onClick={() => navigate("/plans")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to Plans
        </button>
      </div>
    );
  }

  return (
    <div className="embedded-checkout-container">
      {/* ── Left Panel ── */}
      <div className="checkout-left-panel">

        {/* 1–2. Header: back + brand (fixed, does not scroll) */}
        <div className="checkout-left-header">
          <button className="checkout-back-btn" onClick={() => navigate("/plans")}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to plans
          </button>

          <div className="checkout-brand">
            <img src={New_ScrollX2025} alt="ScrollX" className="checkout-brand-logo checkout-brand-logo--icon" />
          </div>
        </div>

        {/* 3. Main content (scrolls on short screens) */}
        <div className="checkout-main-content">

          {/* Eyebrow */}
          <span className="checkout-plan-badge">{plan.type} Plan</span>

          {/* Title */}
          <h1 className="checkout-plan-title">
            {plan.text5 || `${plan.type} Plan`}
          </h1>

          {/* Subtitle */}
          <p className="checkout-plan-subtitle">
            {planSubtitle}
          </p>

          {/* Price */}
          <div className="checkout-price-block">
            <span className="checkout-price-currency">{plan.currency}</span>
            <span className="checkout-price-amount">{plan.price}</span>
            <span className="checkout-price-interval">/ {plan.interval_validity}, after trial</span>
          </div>

          {/* Trial badge */}
          <div className="checkout-trial-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span className="checkout-trial-badge-text">
              {plan.trialText || "3-day free trial. $0 due today."}
            </span>
          </div>

          {/* Summary card */}
          <div className="checkout-summary-card">
            {/* Plan row */}
            <div className="checkout-summary-row">
              <span className="checkout-summary-plan-name">{plan.type} Plan</span>
              <span className="checkout-summary-price">{plan.currency}{plan.price}</span>
            </div>
            <div className="checkout-summary-billed">
              Billed {plan.interval_validity} after trial
            </div>

            <div className="checkout-summary-divider" />

            {/* Features */}
            {features.length > 0 && (
              <ul className="checkout-features-list">
                {features.map((feature, index) => (
                  <li className="checkout-feature-item" key={index}>
                    <span className="checkout-feature-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <span className="checkout-feature-text">{feature}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="checkout-summary-divider" />

            {/* Total */}
            <div className="checkout-total-row">
              <div>
                <div className="checkout-total-label">Total due today</div>
                <div className="checkout-total-sublabel">
                  First charge on day {plan.trialDays ? plan.trialDays + 1 : 4}
                </div>
              </div>
              <span className="checkout-total-amount">{plan.currency}0.00</span>
            </div>
          </div>
        </div>

        {/* 4. Trust badges — pinned to bottom */}
        <div className="checkout-trust-footer">
          <div className="checkout-trust-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span className="checkout-trust-text">Secure checkout</span>
          </div>
          <div className="checkout-trust-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span className="checkout-trust-text">Cancel anytime</span>
          </div>
          {plan.usedBy && (
            <div className="checkout-trust-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span className="checkout-trust-text">{plan.usedBy}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Right Panel: Stripe Embedded Checkout ── */}
      <div className="checkout-right-panel">
        <div className="checkout-stripe-wrapper">
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ fetchClientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </div>
  );
};

export default EmbeddedCheckoutPage;